import { useEffect, useState } from "react";

import styles from "@styles/home-configurations.module.scss";
import { MiniCheck, MiniInfo, SuccessIcon } from "shared/components/Icons";
import { Button, Input, Radio } from "@components";
import { If } from "shared/components/control-statements";
import Availability from "./Availability";

import LayoutDefault from "../../../assets/layout-coupons-default.png";
import LayoutGrid from "../../../assets/layout-coupons-grid.png";

import classNames from "classnames/bind";
import BrandUnits from "./BrandUnits";
const css = classNames.bind(styles);

const LAYOUT_OPTIONS = [
  { key: "DEFAULT", label: "Scroll horizontal", image: LayoutDefault },
  {
    key: "VERTICAL_SCROLL",
    label: "Scroll vertical",
    image: LayoutGrid,
  },
];

const CouponsConfig = ({ data, onClose, onSave, onFinish }) => {
  const [steps] = useState([
    { key: "TYPE", label: "Tipo da seção", show: true },
    { key: "LAYOUT", label: "Preenchimento", show: true },
    { key: "TITLE", label: "Título da seção", show: true },
    {
      key: "BRANDS_UNITS",
      label: "Marcas e unidades",
      show: true,
    },
    { key: "AVAILABILITY", label: "Configurar disponibilidade", show: true },
    { key: "OVERVIEW", label: "Resumo", show: true },
  ]);

  const [configCoupons, setConfigCoupons] = useState({
    type: "COUPONS",
    layout: data?.layout || null,
    title: data?.title || null,
    target: {
      brands: data?.target?.brands || null,
      units: data?.target?.units || null,
      channels: data?.target?.channels || null,
    },
    availability: data?.availability || null,
  });
  const [selectedStep, setSelectedStep] = useState(null);
  const [completedSteps, setCompletedSteps] = useState(["TYPE"]);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const getInitialSelectedStep = () => {
      const stepsWithoutType = completedSteps.filter((step) => step !== "TYPE");
      return stepsWithoutType.at(-1) || "LAYOUT";
    };

    setSelectedStep(getInitialSelectedStep);
  }, []);

  const handleConfig = (key, value) => {
    setConfigCoupons({ ...configCoupons, [key]: value });
  };

  const getVisibleSteps = () => steps.filter((step) => step.show);

  const handleContinue = () => {
    if (selectedStep === "OVERVIEW") {
      onSave(configCoupons);
      setShowSuccess(true);
      return;
    }

    setCompletedSteps((prev) =>
      prev.includes(selectedStep) ? prev : [...prev, selectedStep]
    );

    const visibleSteps = getVisibleSteps();
    const currentIndex = visibleSteps.findIndex(
      (step) => step.key === selectedStep
    );

    const nextStep = visibleSteps[currentIndex + 1];

    if (nextStep) setSelectedStep(nextStep.key);
  };

  const validateStep = () => {
    switch (selectedStep) {
      case "LAYOUT":
        return configCoupons.layout !== null;
      case "TITLE":
        return !!configCoupons.title;
      case "BRANDS_UNITS":
        return true;
      case "AVAILABILITY":
        return (
          configCoupons.availability?.length > 0 ||
          configCoupons.availability === null
        );
      case "OVERVIEW":
        return true;
      default:
        return false;
    }
  };

  const isValidStep = validateStep();

  const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  const formatHour = (hour) => {
    const h = hour.padStart(4, "0").slice(0, 2);
    const m = hour.padStart(4, "0").slice(2);
    return `${h}:${m}`;
  };

  const Horaries = () => {
    if (configCoupons.availability === null) return <span>A todo momento</span>;

    return (
      <div>
        {configCoupons.availability.map((entry, index) => {
          const day = weekDays[entry.weekDay];
          const horaries = entry.times.map((t, idx) => (
            <span key={idx}>
              {formatHour(t.start)} - {formatHour(t.end)}
              {idx < entry.times.length - 1 && " / "}
            </span>
          ));

          return (
            <span key={index} className={styles.time}>
              <strong>{day}:</strong> {horaries}
              {index < configCoupons.availability.length - 1 && " | "}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <If cond={!showSuccess}>
        <div className={styles.homeSidebar}>
          <ul>
            {steps
              .filter(({ show }) => show)
              .map(({ key, label }) => {
                const isSelected = selectedStep === key;
                const isCompleted = completedSteps.includes(key);

                return (
                  <li
                    key={String(key)}
                    className={css({ selected: isSelected })}
                    onClick={() => {
                      if (key === "TYPE") return onClose();
                      if (isCompleted) setSelectedStep(key);
                    }}
                  >
                    {isCompleted ? <MiniCheck /> : <MiniInfo />}
                    <span>{label}</span>
                  </li>
                );
              })}
          </ul>
        </div>
      </If>

      <If cond={!showSuccess}>
        <div className={styles.scrollContainer}>
          <If cond={selectedStep === "LAYOUT"}>
            <div className={styles.configContainer}>
              <p className={styles.title}>Defina o preenchimento</p>
              <div className={styles.options}>
                {LAYOUT_OPTIONS.map((option) => (
                  <div
                    className={styles.option}
                    key={option.key}
                    onClick={() => handleConfig("layout", option.key)}
                  >
                    <div
                      className={css({
                        box: true,
                        selected: configCoupons.layout === option.key,
                      })}
                    >
                      <div className={styles.image}>
                        <img src={option.image} alt={option.label} />
                      </div>
                    </div>

                    <Radio
                      checked={configCoupons.layout}
                      value={option.key}
                      readOnly
                      family="layout"
                    >
                      {option.label}
                    </Radio>
                  </div>
                ))}
              </div>
            </div>
          </If>

          <If cond={selectedStep === "TITLE"}>
            <div className={styles.configContainer}>
              <p className={styles.title}>Nome da seção</p>
              <Input
                className="mt-15"
                autoFocus
                value={configCoupons.title}
                onChange={(e) => handleConfig("title", e.target.value)}
              />
            </div>
          </If>

          <If cond={selectedStep === "BRANDS_UNITS"}>
            <BrandUnits
              data={configCoupons.target}
              onChange={(data) => {
                handleConfig("target", {
                  brands: data.brands || [],
                  units: data.units || [],
                  channels: data.channels || [],
                });
              }}
            />
          </If>

          <If cond={selectedStep === "AVAILABILITY"}>
            <Availability
              availability={configCoupons.availability}
              onChange={(availability) =>
                handleConfig("availability", availability)
              }
            />
          </If>

          <If cond={selectedStep === "OVERVIEW"}>
            <div className={styles.configContainer}>
              <p className={styles.title}>Resumo</p>

              <ul className={styles.resume}>
                <li>
                  <p>Tipo da seção</p>
                  <span>Cupons</span>
                </li>
                <li>
                  <p>Preenchimento</p>
                  <span>
                    {
                      LAYOUT_OPTIONS.find((o) => o.key === configCoupons.layout)
                        ?.label
                    }
                  </span>
                </li>
                <li>
                  <p>Título da seção</p>
                  <span>{configCoupons.title}</span>
                </li>
                <li>
                  <p>Disponibilidade</p>
                  <Horaries />
                </li>
              </ul>
            </div>
          </If>
        </div>

        <div className={styles.footer}>
          <Button
            design="primary"
            onClick={handleContinue}
            disabled={!isValidStep}
          >
            {selectedStep === "OVERVIEW" ? "Confirmar e finalizar" : "Próximo"}
          </Button>
        </div>
      </If>

      <If cond={showSuccess}>
        <div className={styles.successContainer}>
          <div className={styles.successIcon}>
            <SuccessIcon size={14} />
          </div>
          <p className={styles.successMessage}>
            Seção {data?.id ? "atualizada" : "criada"} com sucesso.
          </p>

          <div className={styles.footer}>
            <Button design="primary" onClick={onFinish}>
              Fechar
            </Button>
          </div>
        </div>
      </If>
    </>
  );
};

export default CouponsConfig;
